import React from 'react';

import AdminPageHeader from '../components/common/AdminPageHeader';
import { RELEASE_NOTES } from '../constants/releaseNotes';
import { APP_VERSION } from '../constants/appVersion';
import { useTheme } from '../theme/ThemeContext';
import { formatDayIST } from '../utils/formatIST';

/**
 * AboutPage (OR-138, OR-140) — reachable from the avatar dropdown for every role.
 * Order: About / ArogyaKhata · Version (OR-141) / What's new (only when RELEASE_NOTES has entries) / Help & support / Data sources.
 * Data sources carries the GODL-India §5 attribution for the bundled pincode directory
 * (public/pincode/*.json, built by scripts/build-pincode-data.py).
 * Copy is owner-approved at click-through; add nothing else here. No dot/badge/popup anywhere.
 */
const PINCODE_SOURCE_URL = 'https://data.gov.in/resource/all-india-pincode-directory-till-last-month';
const GODL_URL = 'https://data.gov.in/Godl';
const SUPPORT_EMAIL = 'admin@arogyakhata.com';
const SUPPORT_WHATSAPP_URL = 'https://wa.me/918867591155';
const SUPPORT_TEL_URL = 'tel:+918867591155';

export default function AboutPage() {
  const { isDark } = useTheme();
  const bg = isDark ? 'bg-slate-900' : 'bg-slate-50';
  const cardBg = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const textBody = isDark ? 'text-slate-100' : 'text-slate-900';
  const link = isDark ? 'text-teal-400 underline break-all' : 'text-teal-700 underline break-all';
  const sectionCls = `rounded-xl border p-4 text-sm ${cardBg} ${textBody}`;
  const whatsNew = [...RELEASE_NOTES].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div className={`min-h-screen ${bg}`} data-testid="about-page">
      <AdminPageHeader />
      <div className="mx-auto max-w-xl p-4">
        <h1 className={`mb-1 text-xl font-semibold ${textBody}`}>About</h1>
        <p className={`mb-4 text-sm ${textBody}`} data-testid="about-app-name">ArogyaKhata · Version {APP_VERSION}</p>
        <div className="space-y-4">
          {whatsNew.length > 0 && (
            <section className={sectionCls} data-testid="about-whats-new">
              <h2 className="mb-2 text-base font-semibold">What&apos;s new</h2>
              <ul className="space-y-3">
                {whatsNew.map((note) => (
                  <li key={`${note.date}-${note.title}`} data-testid="about-whats-new-entry">
                    <p className="font-medium">{note.title} <span className="font-normal">· {formatDayIST(note.date)}</span></p>
                    <ul className="list-disc pl-5">
                      {note.points.map((point) => <li key={point}>{point}</li>)}
                    </ul>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section className={sectionCls} data-testid="about-help-support">
            <h2 className="mb-2 text-base font-semibold">Help &amp; support</h2>
            <p>
              Email: <a href={`mailto:${SUPPORT_EMAIL}`} className={link}>{SUPPORT_EMAIL}</a>
            </p>
            <p>
              <a href={SUPPORT_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className={link}>WhatsApp</a>
              {' / '}
              <a href={SUPPORT_TEL_URL} className={link}>Call</a>
              : +91 88675 91155
            </p>
          </section>
          <section className={sectionCls} data-testid="about-data-sources">
            <h2 className="mb-2 text-base font-semibold">Data sources</h2>
            <p>
              Department of Posts, Ministry of Communications, 2019, All India Pincode Directory, Open Government
              Data Platform India, 30/05/2019,{' '}
              <a href={PINCODE_SOURCE_URL} target="_blank" rel="noopener noreferrer" className={link}>{PINCODE_SOURCE_URL}</a>.
              {' '}Published under Government Open Data License – India:{' '}
              <a href={GODL_URL} target="_blank" rel="noopener noreferrer" className={link}>{GODL_URL}</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
